import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { cartService } from '../services/cartService';
import { Cart } from '../types/cart';
import {
  CartAction,
  CartContextType,
  CartProviderProps,
  CartState,
} from '../types/contexts';
import { useConsent } from './ConsentContext';

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_CART':
      return { ...state, cart: action.payload, isLoading: false, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR_CART':
      return { ...state, cart: null, error: null };
    default:
      return state;
  }
};

const initialState: CartState = {
  cart: null,
  isLoading: false,
  error: null,
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { isEnabled: isConsentEnabled } = useConsent();

  // Get or create cart ID
  const getCartId = useCallback(async (): Promise<string> => {
    let cartId = isConsentEnabled('functional')
      ? localStorage.getItem('cartId')
      : (undefined as any);

    if (!cartId) {
      try {
        const cart: Cart = await cartService.createCart();
        cartId = cart.id;
        if (isConsentEnabled('functional')) {
          localStorage.setItem('cartId', cartId);
        }
      } catch (error) {
        throw error;
      }
    }

    return cartId;
  }, [isConsentEnabled]);

  // Load cart on mount
  useEffect(() => {
    const loadCart = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const cartId = await getCartId();
        const cart: Cart = await cartService.getCart(cartId);
        dispatch({ type: 'SET_CART', payload: cart });
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load cart' });
      }
    };

    loadCart();
  }, [getCartId]);

  const addToCart = async (
    productId: string,
    quantity: number = 1,
    selectedMaterialId?: string,
    selectedExtraIds?: string[],
    specialInstructions?: string
  ): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartId = await getCartId();
      const cart: Cart = await cartService.addToCart(cartId, {
        productId,
        quantity,
        selectedMaterialId,
        selectedExtraIds: selectedExtraIds || [],
        specialInstructions,
      });

      dispatch({ type: 'SET_CART', payload: cart });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to add item to cart' });
    }
  };

  const updateCartItem = async (
    productId: string,
    quantity: number,
    selectedMaterialId?: string,
    selectedExtraIds?: string[],
    specialInstructions?: string
  ): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartId = await getCartId();
      const cart: Cart = await cartService.updateCartItem(cartId, {
        productId,
        quantity,
        selectedMaterialId,
        selectedExtraIds: selectedExtraIds || [],
        specialInstructions,
      });

      dispatch({ type: 'SET_CART', payload: cart });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update cart item' });
    }
  };

  const removeFromCart = async (productId: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartId = await getCartId();

      // Remove item from cart (returns void)
      await cartService.removeFromCart(cartId, productId);

      // Refresh cart to get updated state
      const cart: Cart = await cartService.getCart(cartId);
      dispatch({ type: 'SET_CART', payload: cart });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Failed to remove item from cart',
      });
    }
  };

  const clearCart = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartId = await getCartId();

      // Clear cart (returns void)
      await cartService.clearCart(cartId);

      // Refresh cart to get updated state
      const cart: Cart = await cartService.getCart(cartId);
      dispatch({ type: 'SET_CART', payload: cart });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to clear cart' });
    }
  };

  const refreshCart = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartId = await getCartId();
      const cart: Cart = await cartService.getCart(cartId);
      dispatch({ type: 'SET_CART', payload: cart });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to refresh cart' });
    }
  };

  const getCartItemCount = (): number => {
    return state.cart?.totalItems || 0;
  };

  const getCartTotal = (): number => {
    return state.cart?.totalAmount || 0;
  };

  const isInCart = (productId: string): boolean => {
    return (
      state.cart?.items?.some(item => item.productId === productId) || false
    );
  };

  const getCartItemQuantity = (productId: string): number => {
    const item = state.cart?.items?.find(item => item.productId === productId);
    return item?.quantity || 0;
  };

  const value: CartContextType = {
    ...state,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refreshCart,
    getCartItemCount,
    getCartTotal,
    isInCart,
    getCartItemQuantity,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
